/**
 * Helper function to check if two dates are the same day
 */
function isDateInCurrentWeek(inputDate) {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diffToMonday = (dayOfWeek + 6) % 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - diffToMonday);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return inputDate >= monday && inputDate <= sunday;
}
const isSameDay = (date1, date2) => {
    return (date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate());
};
/**
 * Format time in 12-hour format with lowercase am/pm
 */
const formatTime = (date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const meridiem = hours >= 12 ? "pm" : "am";
    // Convert to 12-hour format
    hours = hours % 12;
    // Handle midnight (0 hours in 24-hour time)
    hours = hours ? hours : 12;
    // Add leading zero to minutes if needed
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
    return `${hours}:${minutesStr}${meridiem}`;
};
/**
 * Hook for formatting timestamps according to the specified rules:
 * - "Today" or "Yesterday" with time for recent dates
 * - Day of week with time for dates within the current week
 * - Abbreviated day and month with time for dates older than a week
 * - Includes year only when different from current year
 *
 * @param dateValue The date to format
 * @returns Formatted date string
 */
const useTimestampFormat = (dateValue) => {
    try {
        // Convert input to Date object
        const inputDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
        const now = new Date();
        // Check if date is valid
        if (isNaN(inputDate.getTime())) {
            console.error("Invalid date provided to useTimestampFormat:", dateValue);
            return "Invalid date";
        }
        // Format time in 12-hour format with lowercase am/pm, no leading zeros
        const formattedTime = formatTime(inputDate);
        // Check if it's today
        if (isSameDay(inputDate, now)) {
            return `Today at ${formattedTime}`;
        }
        // Check if it's yesterday
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (isSameDay(inputDate, yesterday)) {
            return `Yesterday at ${formattedTime}`;
        }
        // Check if it's within the current week (within 7 days) AND in the same year
        const oneWeekAgo = new Date(now);
        oneWeekAgo.setDate(now.getDate() - 7);
        // For dates within current week AND in the same year, use full day name
        if (isDateInCurrentWeek(inputDate) &&
            inputDate.getFullYear() === now.getFullYear()) {
            const days = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
            ];
            const dayName = days[inputDate.getDay()];
            return `${dayName} at ${formattedTime}`;
        }
        // For all other dates (future dates or dates outside current week), use abbreviated format
        const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];
        const abbrDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const dayAbbr = abbrDays[inputDate.getDay()];
        const month = months[inputDate.getMonth()];
        const dayOfMonth = inputDate.getDate();
        // Include year only if it's different from current year
        if (inputDate.getFullYear() === now.getFullYear()) {
            return `${dayAbbr}, ${month} ${dayOfMonth} at ${formattedTime}`;
        }
        else {
            return `${dayAbbr}, ${month} ${dayOfMonth}, ${inputDate.getFullYear()} at ${formattedTime}`;
        }
    }
    catch (error) {
        console.error("Error formatting date:", error);
        return "Invalid date";
    }
};

export { useTimestampFormat };
